/**
 * Config-honest plan states for the index and board — Phase 3 has no
 * runtime, so everything derives from config + the clock. "waiting" and
 * "window open" need the recipient-local time of day; without a timezone we
 * degrade to 'enabled' (never guess a clock).
 * For Media Devoted | Created By Ciro Autiero
 */
import { tzOffsetMinutes } from './format';

export type PlanStatusKind =
  | 'no-plan'
  | 'killed'
  | 'disabled'
  | 'waiting'
  | 'window-open'
  | 'enabled';

export interface PlanStatus {
  kind: PlanStatusKind;
  /** For 'waiting': recipient-local minutes when the window next opens. */
  opensAtMin?: number;
}

interface PlanLike {
  enabled: boolean;
  killSwitch: boolean;
  windowStartMin: number;
  windowEndMin: number;
  recipientTimezone: string | null;
}

/** Minutes-from-midnight in a timezone right now, or null when unknown. */
export function nowMinutesIn(
  timeZone: string | null,
  now: Date = new Date(),
): number | null {
  if (!timeZone) return null;
  try {
    const utcMinutes = now.getUTCHours() * 60 + now.getUTCMinutes();
    const offset = tzOffsetMinutes(timeZone, now);
    return (((utcMinutes + offset) % 1440) + 1440) % 1440;
  } catch {
    return null;
  }
}

/** The window may wrap past midnight (start > end = overnight). */
export function isWindowOpen(
  nowMin: number,
  startMin: number,
  endMin: number,
): boolean {
  if (startMin === endMin) return false;
  return startMin < endMin
    ? nowMin >= startMin && nowMin < endMin
    : nowMin >= startMin || nowMin < endMin;
}

export function derivePlanStatus(
  plan: PlanLike | null,
  now: Date = new Date(),
): PlanStatus {
  if (!plan) return { kind: 'no-plan' };
  if (plan.killSwitch) return { kind: 'killed' };
  if (!plan.enabled) return { kind: 'disabled' };
  const nowMin = nowMinutesIn(plan.recipientTimezone, now);
  if (nowMin === null) return { kind: 'enabled' };
  return isWindowOpen(nowMin, plan.windowStartMin, plan.windowEndMin)
    ? { kind: 'window-open' }
    : { kind: 'waiting', opensAtMin: plan.windowStartMin };
}

/** Phase 20 — does any campaign group of the plan auto-send (its master config)? */
export const anyGroupAutoSends = (plan: { masters?: Array<{ autoSend?: { autoSend: boolean } }> }) =>
  (plan.masters ?? []).some((m) => m.autoSend?.autoSend === true);
